import { useState } from "react";
import { Box, Collapse, Divider, Stack, Typography } from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { useSelector } from "react-redux";
import { getDeepViewLineageEntry } from "../../deepViewFieldMap";

/** Both definitions arrive as JSON strings that may be null or malformed */
const parseDefinition = (raw?: string | null): Record<string, any> => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const toArray = (value: any): any[] => {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? value : [value];
};

const monoSx = {
  fontFamily: "monospace",
  fontSize: "12px",
  fontWeight: 700,
  color: "#7B1FA2",
};

const plainSx = {
  fontSize: "12px",
  color: "#00000099",
};

/** White pill holding one filter / group-by expression */
const ExpressionChip = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      border: "1px solid #E0E0E0",
      borderRadius: "6px",
      backgroundColor: "#FFFFFF",
      px: 1,
      py: 0.5,
    }}
  >
    {children}
  </Box>
);

const SectionLabel = ({ label }: { label: string }) => (
  <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
    <Typography
      sx={{
        fontSize: "11px",
        fontWeight: 500,
        color: "#00000099",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {label}
    </Typography>
  </Stack>
);

interface Props {
  /** API key of the column the user clicked, e.g. "total_income_usd" */
  columnKey?: string;
}

const AdvancedFiltersGroupBy = ({ columnKey }: Props) => {
  const [open, setOpen] = useState(false);
  const deepViewData = useSelector(
    (state: any) => state?.deepView?.deepViewData,
  );
  const lineageEntry = getDeepViewLineageEntry(deepViewData, columnKey);

  const filters = Object.entries(
    parseDefinition(lineageEntry?.filtering_definition),
  );
  const groupBy = Object.entries(
    parseDefinition(lineageEntry?.unique_definition),
  );

  if (filters.length === 0 && groupBy.length === 0) return null;

  const renderFilterChip = ([key, definition]: [string, any]) => {
    const values = toArray(definition?.value);
    const formats = toArray(definition?.raw_table_format);
    // Entries carry either a condition/value pair or a source field mapping
    const isUnknownShape =
      !definition?.condition && !definition?.field && formats.length === 0;

    return (
      <ExpressionChip key={key}>
        <Box component="span" sx={monoSx}>
          {key}
        </Box>

        {definition?.condition && (
          <Box component="span" sx={plainSx}>
            {definition.condition}
          </Box>
        )}

        {values.length > 0 && (
          <Box component="span" sx={monoSx}>
            {values.join(", ")}
          </Box>
        )}

        {definition?.field && (
          <>
            <Box component="span" sx={plainSx}>
              uses field
            </Box>
            <Box component="span" sx={monoSx}>
              {definition.field}
            </Box>
          </>
        )}

        {formats.length > 0 && (
          <>
            <Box component="span" sx={plainSx}>
              · format
            </Box>
            <Box component="span" sx={monoSx}>
              {formats.join(", ")}
            </Box>
          </>
        )}

        {isUnknownShape && (
          <Box component="span" sx={monoSx}>
            {JSON.stringify(definition)}
          </Box>
        )}
      </ExpressionChip>
    );
  };

  return (
    <Box mt={1}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onClick={() => setOpen((prev) => !prev)}
        sx={{ cursor: "pointer" }}
      >
        <ArrowRightIcon
          sx={{
            fontSize: 16,
            color: "#00000061",
            transform: open ? "rotate(90deg)" : "none",
            transition: "transform 150ms ease",
          }}
        />
        <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
          Advanced — Filters &amp; Group By
        </Typography>
        <Divider sx={{ flexGrow: 1, borderColor: "#E0E0E0" }} />
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            backgroundColor: "#FAFAFA",
            p: 1.5,
            mt: 1,
          }}
        >
          {filters.length > 0 && (
            <>
              <SectionLabel label="Row filters (filtering_definition)" />

              <Stack
                direction="row"
                flexWrap="wrap"
                gap={1}
                mb={groupBy.length > 0 ? 1.5 : 0}
              >
                {filters.map(renderFilterChip)}
              </Stack>
            </>
          )}

          {groupBy.length > 0 && (
            <>
              <SectionLabel label="Group by (unique_definition)" />

              <Stack direction="row" flexWrap="wrap" gap={1}>
                {groupBy.map(([key, definition]: [string, any]) => (
                  <ExpressionChip key={key}>
                    <Box component="span" sx={monoSx}>
                      GROUP BY
                    </Box>
                    <Box component="span" sx={monoSx}>
                      {definition?.lookup_field ?? "-"}
                    </Box>
                    <Box component="span" sx={plainSx}>
                      → written as
                    </Box>
                    <Box component="span" sx={monoSx}>
                      {key}
                    </Box>
                  </ExpressionChip>
                ))}
              </Stack>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AdvancedFiltersGroupBy;
